// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'

// Styles
import themeColors from '@/assets/theme/colors'
import breakpoints from '@/assets/theme/breakpoints'
import classes from './LegalNoticeView-CSS'

export default function LegalNoticeView() {
  const { t } = useTranslation('legalNotice')
  const { screenDimensions } = useContext(GlobalContext)

  const wide = screenDimensions.width > breakpoints.mobileSmall
  const bodySize = wide ? '0.93rem' : '0.85rem'

  const h2 = (text: string) => (
    <Typography
      variant="h2"
      component="h2"
      sx={classes.h2}
      style={{ fontSize: wide ? '1.1rem' : '0.95rem' }}
    >
      {text}
    </Typography>
  )

  const p = (text: string) => (
    <Typography
      key={text}
      component="p"
      sx={classes.b2}
      style={{ fontSize: bodySize }}
    >
      {text}
    </Typography>
  )

  /** "Label: value" row, where the label is what you scan for. */
  const row = (label: string, value: string) => (
    <Typography
      key={label + value}
      component="p"
      sx={classes.b2}
      style={{ fontSize: bodySize }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        {label}
      </Box>{' '}
      {value}
    </Typography>
  )

  const address = [
    t('sections.company.lines.01'),
    t('sections.company.lines.02'),
    t('sections.company.lines.03'),
    t('sections.company.lines.04'),
    t('sections.company.lines.05')
  ]

  const details: [string, string][] = [
    [
      t('sections.details.items.01.label'),
      t('sections.details.items.01.value')
    ],
    [
      t('sections.details.items.02.label'),
      t('sections.details.items.02.value')
    ],
    [
      t('sections.details.items.03.label'),
      t('sections.details.items.03.value')
    ],
    [
      t('sections.details.items.04.label'),
      t('sections.details.items.04.value')
    ],
    [t('sections.details.items.05.label'), t('sections.details.items.05.value')]
  ]

  return (
    <Box sx={classes.mainDiv}>
      <Box
        sx={classes.sndDiv}
        style={{ minHeight: screenDimensions.height - 220 }}
      >
        <Typography
          variant="h1"
          sx={classes.h1}
          style={{ fontSize: wide ? '2rem' : '1.5rem' }}
        >
          {t('h1')}
        </Typography>

        <Typography
          component="p"
          sx={{ ...classes.b1, marginBottom: '1.5rem' }}
        >
          {t('update')}
        </Typography>

        <Box component="section">
          {h2(t('sections.company.title'))}
          <Box sx={{ marginBottom: '0.75rem' }}>
            {address.map(line => (
              <Typography
                key={line}
                component="p"
                sx={{ color: themeColors.gray.mid }}
                style={{ fontSize: bodySize }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box component="section">
          {h2(t('sections.contact.title'))}
          {row(
            t('sections.contact.email.label'),
            t('sections.contact.email.value')
          )}
          {row(
            t('sections.contact.phone.label'),
            t('sections.contact.phone.value')
          )}
        </Box>

        <Box component="section">
          {h2(t('sections.details.title'))}
          {details.map(([label, value]) => row(label, value))}
        </Box>

        <Box component="section">
          {h2(t('sections.security.title'))}
          {p(t('sections.security.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.responsibility.title'))}
          {p(t('sections.responsibility.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.copyright.title'))}
          {p(t('sections.copyright.p1'))}
          {p(t('sections.copyright.p2'))}
        </Box>

        <Box component="section">
          {h2(t('sections.externalLinks.title'))}
          {p(t('sections.externalLinks.p1'))}
        </Box>
      </Box>
    </Box>
  )
}
